/**
 * The staff tools that replace SOTG's control panel (Builder draft Amendment 1,
 * docs/pdlc/builder-amendment-1-sotg-retirement.md, R1).
 *
 * Access is internal staff plus one feature flag per tool (decided 2026-10-29):
 * no role model. Each flag is read on the server by `staffToolGate`, and an
 * unreadable flag counts as off, so a tool is never opened by a flag-service
 * blip. A flag key has to exist in the flag admin before the tool can be
 * turned on for anyone.
 */

export interface StaffTool {
    /** Stable id, written to `pdlc_staff_action.tool`. */
    key: string;
    /** The feature flag that opens it, per person. */
    flag: string;
    /** What staff see in the Tools menu. */
    label: string;
    /** The work package and ticket that builds it, or the spec, for a tool outside the SOTG work packages. */
    ticket: string;
    /** Its page, once one ships; the Surveys menu lists only tools with one. */
    href?: string;
    /** The flag admin's description, when the generic staff-tool sentence does not fit. */
    description?: string;
}

export const STAFF_TOOLS = [
    {
        key: 'search',
        flag: 'staff-search',
        label: 'Search',
        ticket: 'W1.4 M4S-5967',
        href: '/tools/staff-search',
    },
    {
        key: 'reference-data',
        flag: 'staff-reference-data',
        label: 'Reference data',
        ticket: 'W1.5 M4S-5968',
        href: '/tools/staff-reference',
    },
    {
        key: 'fielding-console',
        flag: 'staff-fielding-console',
        label: 'Fielding',
        ticket: 'W3.4 M4S-5975',
        href: '/tools/staff-fielding',
    },
    {
        key: 'projects',
        flag: 'staff-projects',
        label: 'Projects',
        ticket: 'W3.5 M4S-5976',
        href: '/tools/staff-projects',
    },
    {
        key: 'panelists',
        flag: 'staff-panelists',
        label: 'Panelists',
        ticket: 'W4.1 M4S-5978',
        href: '/tools/staff-panelists',
    },
    {
        key: 'release-groups',
        flag: 'staff-release-groups',
        label: 'Release groups',
        ticket: 'W5.1 M4S-5980',
        href: '/tools/staff-release-groups',
    },
    {
        key: 'location-groups',
        flag: 'staff-location-groups',
        label: 'Location groups',
        ticket: 'W5.3 M4S-5982',
        href: '/tools/staff-location-groups',
    },
    {
        key: 'scrubber',
        flag: 'staff-scrubber',
        label: 'Data scrubber',
        ticket: 'W6.1 M4S-5983',
        href: '/tools/staff-scrubber',
    },
    // On each survey of the project view; no page of its own.
    { key: 'exports', flag: 'staff-exports', label: 'Survey exports', ticket: 'W6.2 M4S-5984' },
    // The Earned Schema Layout, behind its own flag as SOTG kept it behind its own permission.
    {
        key: 'exports-earned',
        flag: 'staff-exports-earned',
        label: 'Earned export layout',
        ticket: 'W6.2 M4S-5984',
    },
    {
        key: 'data-corrections',
        flag: 'staff-data-corrections',
        label: 'Data corrections',
        ticket: 'W6.3 M4S-5985',
        href: '/tools/staff-corrections',
    },
    {
        key: 'panelist-payments',
        flag: 'staff-panelist-payments',
        label: 'Panelist payments',
        ticket: 'W7.1 M4S-5989',
    },
    // The agents app's admin pages, rebuilt here (docs/pdlc/agents-admin.md). It has its own
    // Agents menu (src/lib/staff-tools/agents/pages.ts), so no href for the Surveys menu.
    {
        key: 'agents',
        flag: 'staff-agents',
        label: 'Agents',
        ticket: 'docs/pdlc/agents-admin.md',
        description:
            'Opens the Agents menu in the Admin dashboard for this person: scheduled jobs, agent usage, OOH campaigns, skills and memories. Internal staff only; the server checks it again on every request, and off or unreadable keeps the pages closed.',
    },
] as const satisfies readonly StaffTool[];

export type StaffToolKey = (typeof STAFF_TOOLS)[number]['key'];

export const STAFF_TOOL_FLAGS: string[] = STAFF_TOOLS.map((t) => t.flag);

export function staffTool(key: StaffToolKey): StaffTool {
    const tool = STAFF_TOOLS.find((t) => t.key === key);
    if (!tool) throw new Error(`Unknown staff tool ${key}`);
    return tool;
}
